/**
 * BankStatementUploadModal — Modal de 3 fases para subir cartola bancaria con mapper dinámico.
 * Fase 1: Subir archivo + saldos  →  Fase 2: Mapear columnas  →  Fase 3: Resultado
 */
import { useState, useRef, useCallback, useMemo } from 'react'
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2, ArrowLeft, ArrowRight, Columns3 } from 'lucide-react'
import {
  parseCSVPreview, autoMatch, detectDateFormat, DATE_FORMATS,
  type SystemField,
} from '@/lib/csvUtils'
import { DataTable } from '@/components/ds/DataTable'
import { useUploadBankStatement } from '../hooks/useAccounting'
import { Modal } from '@/components/ds/Modal'

/** Una columna del archivo, con su preview ya resuelto por posición. */
interface FilaDeColumnaCsv {
  header: string
  previews: string[]
}

// ─── Bank statement system fields ────────────────────────────────────────────

const BANK_STATEMENT_FIELDS: SystemField[] = [
  {
    key: 'date', label: 'Fecha', required: true,
    aliases: ['fecha', 'date', 'fecha_transaccion', 'fecha_movimiento', 'fecha_operacion'],
  },
  {
    key: 'description', label: 'Descripción', required: true,
    aliases: ['descripcion', 'glosa', 'detalle', 'concepto', 'description'],
  },
  {
    key: 'reference', label: 'Referencia',
    aliases: ['referencia', 'ref', 'numero', 'nro', 'num_documento', 'reference', 'n_documento'],
  },
  {
    key: 'amount', label: 'Monto',
    aliases: ['monto', 'importe', 'valor', 'total', 'amount'],
  },
  {
    key: 'debit', label: 'Cargo / Débito',
    aliases: ['cargo', 'debito', 'debe', 'egreso', 'debit', 'cargos'],
  },
  {
    key: 'credit', label: 'Abono / Crédito',
    aliases: ['abono', 'credito', 'haber', 'ingreso', 'credit', 'abonos'],
  },
]

// ─── Types ───────────────────────────────────────────────────────────────────

interface BankStatementUploadModalProps {
  open: boolean
  onClose: () => void
  reconciliationId: string
  onSuccess: () => void
}

type Phase = 'upload' | 'map' | 'result'

// ─── Component ───────────────────────────────────────────────────────────────

export function BankStatementUploadModal({
  open, onClose, reconciliationId, onSuccess,
}: BankStatementUploadModalProps) {
  const [phase, setPhase] = useState<Phase>('upload')
  const [file, setFile] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [csvHeaders, setCsvHeaders] = useState<string[]>([])
  const [previewRows, setPreviewRows] = useState<string[][]>([])
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [separator, setSeparator] = useState(',')
  const [skipRows, setSkipRows] = useState(0)
  const [balanceOpening, setBalanceOpening] = useState('')
  const [balanceClosing, setBalanceClosing] = useState('')
  const [dateFormat, setDateFormat] = useState('DD/MM/YYYY')
  const [linesImported, setLinesImported] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const uploadMutation = useUploadBankStatement()

  const reset = useCallback(() => {
    setPhase('upload')
    setFile(null)
    setCsvHeaders([])
    setPreviewRows([])
    setMapping({})
    setSeparator(',')
    setSkipRows(0)
    setBalanceOpening('')
    setBalanceClosing('')
    setDateFormat('DD/MM/YYYY')
    setLinesImported(0)
    setError(null)
  }, [])

  const handleClose = () => {
    reset()
    onClose()
  }

  // ─── Phase 1: Upload ──────────────────────────────────────────────────────

  const handleFile = (f: File) => {
    if (!f.name.toLowerCase().endsWith('.csv')) {
      setError('Solo se permiten archivos .csv')
      return
    }
    setError(null)
    setFile(f)

    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = ev.target?.result as string
      const { headers, rows, detectedSeparator, skipRows: detectedSkip } = parseCSVPreview(text)
      if (headers.length === 0) {
        setError('El archivo CSV está vacío o no tiene cabeceras.')
        return
      }
      setCsvHeaders(headers)
      setPreviewRows(rows)
      setSeparator(detectedSeparator)
      setSkipRows(detectedSkip)
      const auto = autoMatch(headers, BANK_STATEMENT_FIELDS)
      setMapping(auto)
      // Auto-detect date format from the date column preview values
      const dateKey = Object.entries(auto).find(([, v]) => v === 'date')?.[0]
      if (dateKey) {
        const dateIdx = headers.indexOf(dateKey)
        if (dateIdx >= 0) {
          const sampleDates = rows.map(r => r[dateIdx] || '').filter(Boolean)
          setDateFormat(detectDateFormat(sampleDates))
        }
      }
      setPhase('map')
    }
    reader.readAsText(f)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files[0]
    if (f) handleFile(f)
  }

  // ─── Phase 2: Mapping ─────────────────────────────────────────────────────

  const updateMapping = (csvHeader: string, fieldKey: string) => {
    setMapping(prev => {
      const next = { ...prev }
      if (fieldKey === '') {
        delete next[csvHeader]
      } else {
        for (const [k, v] of Object.entries(next)) {
          if (v === fieldKey && k !== csvHeader) delete next[k]
        }
        next[csvHeader] = fieldKey
      }
      return next
    })
  }

  const mappedValues = useMemo(() => new Set(Object.values(mapping)), [mapping])

  /**
   * Las filas de la tabla de correspondencia, con su preview ya resuelto.
   *
   * ⚠️ El preview sale de la POSICIÓN de la columna en el archivo
   * (`previewRows[fila][idx]`), así que se calcula acá y viaja DENTRO del dato.
   * Hacerlo en el `render` del `DataTable` usaría el índice de la fila ya
   * ordenada, y el día que alguien marque una columna `sortable` cada preview
   * mostraría los valores de otra columna — sin ningún error.
   */
  const columnasDelCsv = useMemo<FilaDeColumnaCsv[]>(
    () => csvHeaders.map((header, idx) => ({
      header,
      previews: previewRows.map(r => r[idx] || '').filter(Boolean).slice(0, 2),
    })),
    [csvHeaders, previewRows],
  )

  const validationError = useMemo(() => {
    if (!mappedValues.has('date')) return 'Falta mapear el campo Fecha'
    if (!mappedValues.has('description')) return 'Falta mapear el campo Descripción'
    if (!mappedValues.has('amount') && !(mappedValues.has('debit') && mappedValues.has('credit'))) {
      return 'Mapea Monto, o bien Cargo y Abono'
    }
    return null
  }, [mappedValues])

  const canImport = !validationError

  // ─── Phase 3: Import ──────────────────────────────────────────────────────

  const handleImport = async () => {
    if (!file || !canImport) return
    setError(null)
    try {
      const res = await uploadMutation.mutateAsync({
        id: reconciliationId,
        file,
        balanceOpening: balanceOpening || '0',
        balanceClosing: balanceClosing || '0',
        mapping,
        separator,
        dateFormat,
        skipRows,
      })
      setLinesImported(res.lines_imported)
      setPhase('result')
    } catch (e: any) {
      const msg = e?.response?.data?.error?.message || e?.message || 'Error al importar.'
      setError(msg)
    }
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  if (!open) return null

  return (
    <Modal
      open
      onClose={handleClose}
      size="lg"
      title={
        <span className="flex items-center gap-2">
          <FileSpreadsheet size={18} className="text-primary shrink-0" />
          Subir Cartola Bancaria
        </span>
      }
      subtitle={
        <>
          {phase === 'upload' && (
            <span className="text-xs text-content-subtle bg-surface-muted px-2 py-0.5 rounded-full">
              Paso 1/3 — Archivo
            </span>
          )}
          {phase === 'map' && (
            <span className="text-xs text-content-subtle bg-surface-muted px-2 py-0.5 rounded-full">
              Paso 2/3 — Mapear columnas
            </span>
          )}
          {phase === 'result' && (
            <span className="text-xs text-success-content-soft bg-success-surface px-2 py-0.5 rounded-full">
              Paso 3/3 — Resultado
            </span>
          )}
        </>
      }
      actions={
        <>
          <div className="flex flex-1 flex-wrap items-center justify-between gap-2">
          <div>
            {phase === 'map' && (
              <button onClick={() => { setPhase('upload'); setFile(null); setCsvHeaders([]); setPreviewRows([]); setMapping({}) }}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
                <ArrowLeft size={14} />
                Volver
              </button>
            )}
          </div>

          <div className="flex gap-3">
            {phase === 'result' ? (
              <button onClick={() => { handleClose(); onSuccess() }}
                className="px-4 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors">
                Cerrar
              </button>
            ) : phase === 'upload' ? (
              <button onClick={handleClose}
                className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
                Cancelar
              </button>
            ) : (
              <>
                <button onClick={handleClose}
                  className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
                  Cancelar
                </button>
                <button onClick={handleImport} disabled={!canImport || uploadMutation.isPending}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors">
                  {uploadMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                  Importar
                </button>
              </>
            )}
          </div>
          </div>
        </>
      }
    >
      <div className="space-y-4">
        {/* ── Phase 1: Upload ─────────────────────────────────────────── */}
        {phase === 'upload' && (
          <>
            <p className="text-sm text-content-muted">
              Selecciona el archivo CSV de tu cartola bancaria. Se detectará automáticamente el formato y separador.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="bankstatementuploadmodal-saldo-inicial" className="block text-xs font-medium text-content-muted mb-1">Saldo Inicial</label>
                <input id="bankstatementuploadmodal-saldo-inicial"
                  type="number"
                  step="1"
                  value={balanceOpening}
                  onChange={e => setBalanceOpening(e.target.value)}
                  placeholder="0.00"
                  className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                />
              </div>
              <div>
                <label htmlFor="bankstatementuploadmodal-saldo-final" className="block text-xs font-medium text-content-muted mb-1">Saldo Final</label>
                <input id="bankstatementuploadmodal-saldo-final"
                  type="number"
                  step="1"
                  value={balanceClosing}
                  onChange={e => setBalanceClosing(e.target.value)}
                  placeholder="0.00"
                  className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
                />
              </div>
            </div>

            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
                ${dragOver ? 'border-primary-200 bg-primary-50' : 'border-divider hover:border-divider-strong hover:bg-soft'}`}
            >
              <Upload size={28} className="mx-auto mb-2 text-content-subtle" />
              <p className="text-sm text-content-muted">Arrastra un archivo CSV aquí o haz clic para seleccionar</p>
              <p className="text-xs text-content-subtle mt-1">Se detecta automáticamente separador coma (,) o punto y coma (;)</p>
              <input ref={inputRef} type="file" accept=".csv" className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = '' }} />
            </div>
          </>
        )}

        {/* ── Phase 2: Mapping ───────────────────────────────────────── */}
        {phase === 'map' && (
          <>
            <div className="flex items-center justify-between gap-2 text-sm text-content-muted">
              <div className="flex items-center gap-2">
                <Columns3 size={14} className="text-content-subtle" />
                <span>
                  Archivo: <strong>{file?.name}</strong> — {csvHeaders.length} columnas
                  {separator === ';' && <span className="ml-1 text-xs text-warning-content-soft">(sep: ;)</span>}
                  {skipRows > 0 && (
                    <span className="ml-1 text-xs text-blue-600 dark:text-blue-300">
                      (se omitieron {skipRows} {skipRows === 1 ? 'fila' : 'filas'} de preámbulo)
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <label htmlFor="bankstatementuploadmodal-formato-fecha" className="text-xs text-content-muted whitespace-nowrap">Formato fecha:</label>
                <select id="bankstatementuploadmodal-formato-fecha"
                  value={dateFormat}
                  onChange={e => setDateFormat(e.target.value)}
                  className="text-xs border border-divider rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {DATE_FORMATS.map(f => (
                    <option key={f.pattern} value={f.pattern}>{f.label} ({f.example})</option>
                  ))}
                </select>
              </div>
            </div>

            <DataTable<FilaDeColumnaCsv>
              columns={[
                { key: 'header', header: 'Columna CSV', width: '25%',
                  render: f => (
                    <span className="font-mono text-xs bg-surface-muted px-1.5 py-0.5 rounded text-content">
                      {f.header}
                    </span>
                  ) },
                { key: 'previews', header: 'Preview', width: '40%',
                  render: f => (
                    <div className="flex flex-wrap gap-1">
                      {f.previews.map((v, i) => (
                        <span key={i} className="text-xs text-content-subtle bg-surface-muted px-1.5 py-0.5 rounded truncate max-w-[140px]">
                          {v}
                        </span>
                      ))}
                      {f.previews.length === 0 && <span className="text-xs text-content-subtle italic">vacío</span>}
                    </div>
                  ) },
                { key: 'campo', header: 'Campo del sistema', width: '35%',
                  render: f => {
                    const seleccionado = mapping[f.header] || ''
                    return (
                      <select
                        value={seleccionado}
                        onChange={e => updateMapping(f.header, e.target.value)}
                        aria-label={`Campo del sistema para la columna ${f.header}`}
                        className={`w-full text-sm border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary transition-colors
                          ${seleccionado ? 'border-primary-200 bg-primary-50/50 text-primary-hover' : 'border-divider text-content-muted'}`}
                      >
                        <option value="">— No importar —</option>
                        {BANK_STATEMENT_FIELDS.map(sf => {
                          const yaUsado = Object.entries(mapping).some(
                            ([k, v]) => v === sf.key && k !== f.header
                          )
                          return (
                            <option key={sf.key} value={sf.key} disabled={yaUsado}>
                              {sf.label}{sf.required ? ' *' : ''}{yaUsado ? ' (ya asignado)' : ''}
                            </option>
                          )
                        })}
                      </select>
                    )
                  } },
              ]}
              rows={columnasDelCsv}
              rowKey={f => f.header}
              caption="Correspondencia entre las columnas del archivo y los campos del sistema"
            />

            {validationError && (
              <div className="flex items-start gap-2 p-3 bg-warning-surface border border-warning-border rounded-lg">
                <AlertTriangle size={16} className="text-warning-solid mt-0.5 shrink-0" />
                <p className="text-sm text-warning-content-strong">{validationError}</p>
              </div>
            )}
          </>
        )}

        {/* ── Phase 3: Result ────────────────────────────────────────── */}
        {phase === 'result' && (
          <div className="flex items-start gap-2 p-3 bg-success-surface border border-success-border rounded-lg">
            <CheckCircle2 size={16} className="text-success-solid mt-0.5 shrink-0" />
            <div className="text-sm text-success-content-strong">
              <p className="font-medium">Cartola importada exitosamente</p>
              <p className="mt-1">
                <strong>{linesImported}</strong> líneas importadas.
              </p>
            </div>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div className="flex items-start gap-2 p-3 bg-danger-surface border border-danger-border rounded-lg">
            <AlertTriangle size={16} className="text-danger-solid mt-0.5 shrink-0" />
            <p className="text-sm text-danger-content">{error}</p>
          </div>
        )}
      </div>
    </Modal>
  )
}
